import * as React from 'react';
import { Select, Spin, Switch, Tooltip } from 'antd';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import DatePicker from 'react-multi-date-picker';
import persian from 'react-date-object/calendars/persian';
import persian_fa from 'react-date-object/locales/persian_fa';
import { TableColumn } from 'react-data-table-component';

import { Badge } from 'components/badge/badge';
import { Button, buttonType } from 'components/button/button';
import { CopyablePhone } from 'components/copyable-phone/copyable-phone';
import { Input } from 'components/input/input';
import { Table } from 'components/table/table';
import { UserLink } from 'components/user-link/user-link';
import { ReactComponent as CalendarIcon } from 'assets/icons/dash-line-icons/calendar.svg';
import {
    MarketingActivity,
    MarketingActivitySource,
    MarketingActivityStatus,
    MarketingActivityType,
    PaginationList,
    activitySourceTitles,
    activityStatusBadges,
    activityTypeTitles,
} from 'core/domain';
import { getMarketingActivities } from 'core/repositories/marketing';
import { jFormat, jFormatEndOfDay } from 'core/utils/jFormat';
import { formatDate, toQuery } from '../marketing-utils';
import { CreateActivityModal, UpdateActivityModal } from './activity-modals';
import '../marketing.scss';

const { useEffect, useState } = React;

interface Filters {
    keyword: string;
    type?: MarketingActivityType;
    status?: MarketingActivityStatus;
    source?: MarketingActivitySource;
    onlyMine: boolean;
    from: string;
    to: string;
}

const emptyFilters: Filters = { keyword: '', onlyMine: false, from: '', to: '' };

const toOptions = (titles: Record<number, string>) =>
    Object.entries(titles).map(([value, label]) => ({ value: Number(value), label }));

const typeOptions = toOptions(activityTypeTitles);
const sourceOptions = toOptions(activitySourceTitles);
const statusOptions = Object.entries(activityStatusBadges).map(([value, badge]) => ({
    value: Number(value),
    label: badge.text,
}));

interface Props {
    /** فقط اکتیویتی‌های این کاربر؛ دکمه‌ی «ثبت اکتیویتی» هم فقط در این حالت نمایش داده می‌شود. */
    userId?: string;
    /** فیلترهای ثابت (مثلا تب «در حال انجام» تسک‌های تماس) که در فرم فیلتر نمایش داده نمی‌شوند. */
    fixedType?: MarketingActivityType;
    fixedStatus?: MarketingActivityStatus;
    /** متن راهنمای بالای جدول. */
    hint?: string;
    /** با تغییر این مقدار، لیست دوباره بارگذاری می‌شود. */
    reloadKey?: number;
}

export const ActivitiesPanel: React.FC<Props> = ({ userId, fixedType, fixedStatus, hint, reloadKey }) => {
    const [filters, setFilters] = useState<Filters>(emptyFilters);
    const [query, setQuery] = useState('');
    const [data, setData] = useState<PaginationList<MarketingActivity>>();
    const [loading, setLoading] = useState(false);
    const [pageSize, setPageSize] = useState(10);
    const [createOpen, setCreateOpen] = useState(false);
    const [editing, setEditing] = useState<Pick<MarketingActivity, 'id' | 'type' | 'status' | 'description'>>();

    const load = (nextFilters: Filters, page = 1) => {
        const nextQuery = toQuery({
            userId,
            type: fixedType ?? nextFilters.type,
            status: fixedStatus ?? nextFilters.status,
            source: nextFilters.source,
            onlyMine: nextFilters.onlyMine,
            keyword: nextFilters.keyword.trim(),
            startedDate: nextFilters.from ? jFormat(nextFilters.from) : '',
            endDate: nextFilters.to ? jFormatEndOfDay(nextFilters.to) : '',
        });
        setQuery(nextQuery);
        getMarketingActivities(setLoading, setData, page, pageSize, nextQuery);
    };

    const reloadCurrentPage = () =>
        getMarketingActivities(setLoading, setData, data?.pagination?.pageIndex || 1, pageSize, query);

    useEffect(() => {
        load(filters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, fixedType, fixedStatus, reloadKey]);

    const columns: TableColumn<MarketingActivity>[] = [
        {
            name: 'تاریخ',
            id: 'createdOn',
            selector: (row) => row.createdOn,
            cell: (row) => <span>{formatDate(row.createdOn)}</span>,
            minWidth: '130px',
        },
        ...(userId
            ? []
            : [
                  {
                      name: 'کاربر',
                      id: 'user',
                      selector: (row: MarketingActivity) => row.phoneNumber ?? '',
                      cell: (row: MarketingActivity) => (
                          <div className="mk-user-cell">
                              <UserLink userId={row.userId}>{row.fullName?.trim() || 'بدون نام'}</UserLink>
                              <CopyablePhone phoneNumber={row.phoneNumber} />
                          </div>
                      ),
                      minWidth: '160px',
                  },
              ]),
        {
            name: 'نوع',
            id: 'type',
            selector: (row) => row.type,
            cell: (row) => <span>{activityTypeTitles[row.type] ?? '-'}</span>,
            width: '80px',
        },
        {
            name: 'وضعیت',
            id: 'status',
            selector: (row) => row.status,
            cell: (row) => {
                const badge = activityStatusBadges[row.status];
                return badge ? <Badge text={badge.text} type={badge.type} /> : <span>-</span>;
            },
            width: '120px',
        },
        {
            name: 'منبع',
            id: 'source',
            selector: (row) => row.source,
            cell: (row) => (
                <div className="mk-user-cell">
                    <span>{activitySourceTitles[row.source] ?? '-'}</span>
                    {row.campaignTitle && <small>{row.campaignTitle}</small>}
                </div>
            ),
            minWidth: '110px',
        },
        {
            name: 'توضیحات',
            id: 'description',
            selector: (row) => row.description ?? '',
            cell: (row) =>
                row.description ? (
                    <Tooltip title={<span style={{ whiteSpace: 'pre-wrap' }}>{row.description}</span>}>
                        <span className="mk-text-cell">{row.description}</span>
                    </Tooltip>
                ) : (
                    <span>-</span>
                ),
            minWidth: '200px',
            grow: 2,
        },
        {
            name: 'کارشناس',
            id: 'operatorName',
            selector: (row) => row.operatorName ?? '',
            cell: (row) => <span>{row.operatorName || '-'}</span>,
            minWidth: '110px',
        },
        {
            name: 'انجام',
            id: 'doneOn',
            selector: (row) => row.doneOn ?? '',
            cell: (row) => <span>{formatDate(row.doneOn)}</span>,
            minWidth: '130px',
        },
        {
            name: '',
            id: 'action',
            width: '60px',
            cell: (row) =>
                // اکتیویتی‌های پیامک خودکارند و وضعیتشان از نتیجه‌ی ارسال می‌آید.
                row.type !== MarketingActivityType.Sms && (
                    <div className="actions">
                        <Tooltip title="ثبت نتیجه / تغییر وضعیت">
                            <EditOutlined onClick={() => setEditing(row)} />
                        </Tooltip>
                    </div>
                ),
        },
    ];

    return (
        <div className="mk-panel">
            {userId && (
                <CreateActivityModal
                    isOpen={createOpen}
                    setIsOpen={setCreateOpen}
                    userId={userId}
                    onSaved={() => load(filters)}
                />
            )}
            <UpdateActivityModal activity={editing} setActivity={setEditing} onSaved={reloadCurrentPage} />

            {(hint || userId) && (
                <div className="mk-panel__toolbar">
                    <p>{hint}</p>
                    {userId && (
                        <Button
                            type={buttonType.info}
                            text="ثبت اکتیویتی"
                            icon={<PlusOutlined />}
                            onClick={() => setCreateOpen(true)}
                        />
                    )}
                </div>
            )}

            <div className="mk-panel__filters">
                {!userId && (
                    <Input
                        value={filters.keyword}
                        placeholder="شماره موبایل یا نام کاربر"
                        onPressEnter={() => load(filters)}
                        onChange={(e) => setFilters({ ...filters, keyword: e.target.value })}
                    />
                )}
                {fixedType === undefined && (
                    <Select
                        allowClear
                        placeholder="نوع"
                        value={filters.type}
                        options={typeOptions}
                        onChange={(type) => setFilters({ ...filters, type })}
                    />
                )}
                {fixedStatus === undefined && (
                    <Select
                        allowClear
                        placeholder="وضعیت"
                        value={filters.status}
                        options={statusOptions}
                        onChange={(status) => setFilters({ ...filters, status })}
                    />
                )}
                <Select
                    allowClear
                    placeholder="منبع"
                    value={filters.source}
                    options={sourceOptions}
                    onChange={(source) => setFilters({ ...filters, source })}
                />
                <DatePicker
                    value={filters.from}
                    calendar={persian}
                    locale={persian_fa}
                    render={
                        <div>
                            <Input value={filters.from} icon={<CalendarIcon />} placeholder="از تاریخ" readOnly />
                        </div>
                    }
                    onChange={(date: any) => setFilters({ ...filters, from: date ? date.format() : '' })}
                />
                <DatePicker
                    value={filters.to}
                    calendar={persian}
                    locale={persian_fa}
                    render={
                        <div>
                            <Input value={filters.to} icon={<CalendarIcon />} placeholder="تا تاریخ" readOnly />
                        </div>
                    }
                    onChange={(date: any) => setFilters({ ...filters, to: date ? date.format() : '' })}
                />
                <label className="mk-switch">
                    <Switch
                        checked={filters.onlyMine}
                        onChange={(onlyMine) => setFilters({ ...filters, onlyMine })}
                    />
                    فقط اکتیویتی‌های من
                </label>
                <div className="mk-filter-buttons">
                    <Button
                        type={buttonType.success}
                        text="اعمال فیلتر"
                        disabled={loading}
                        onClick={() => load(filters)}
                    />
                    <Button
                        type={buttonType.error}
                        text="حذف فیلتر"
                        disabled={loading}
                        onClick={() => {
                            setFilters(emptyFilters);
                            load(emptyFilters);
                        }}
                    />
                </div>
            </div>

            <Spin spinning={loading}>
                {data?.items?.length ? (
                    <Table
                        blur={loading}
                        data={data.items}
                        column={columns}
                        pagination={data.pagination}
                        pageSize={pageSize}
                        setPageSize={setPageSize}
                        setloader={setLoading}
                        setdata={setData}
                        getdata={getMarketingActivities}
                        filters={query}
                    />
                ) : (
                    <p className="mk-empty">{loading ? 'در حال بارگذاری...' : 'اکتیویتی‌ای یافت نشد.'}</p>
                )}
            </Spin>
        </div>
    );
};
